import { useEffect, useRef, useState } from 'react';
import { Pause, Play, Square, Timer as TimerIcon } from 'lucide-react';
import { useTimerStore, type TimerStopPayload } from '../../store/useTimerStore';
import { deriveElapsedMs } from '../../services/timeTracking/timerMath';
import { useAuthStore } from '../../store/useAuthStore';
import { canAccessFeature, asCollaboratorRole } from '../../services/auth/permissions';
import { TimerStartPopover } from './TimerStartPopover';
import { TimeEntryModal } from './TimeEntryModal';
import type { TimerPrefill } from './timeEntryPrefill';

function fmt(ms: number): string {
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(h)}:${p(m)}:${p(s)}`;
}

export function FloatingTimer() {
  const store = useTimerStore();
  const rawRole = useAuthStore((s) => s.collaborator?.role);
  const role = asCollaboratorRole(rawRole);
  const [, force] = useState(0);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [pendingStop, setPendingStop] = useState<TimerStopPayload | null>(null);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const savedRef = useRef(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (store.status === 'running') {
      tickRef.current = setInterval(() => force((n) => n + 1), 1000);
      return () => { if (tickRef.current) clearInterval(tickRef.current); };
    }
    return;
  }, [store.status]);

  useEffect(() => {
    if (!popoverOpen) return;
    function onClick(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) setPopoverOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [popoverOpen]);

  if (!role || !canAccessFeature(role, 'timer_use')) return null;

  const elapsed = deriveElapsedMs(
    { status: store.status, startedAt: store.startedAt, pauses: store.pauses },
    Date.now(),
  );

  const prefill: TimerPrefill | null = pendingStop
    ? {
        dossierId: pendingStop.dossierId,
        taskId: pendingStop.taskId,
        startedAt: pendingStop.startedAt,
        durationMinutes: pendingStop.durationMinutes,
      }
    : null;

  return (
    <>
      <div ref={containerRef} className="relative">
        {store.status === 'idle' && (
          <button
            onClick={() => setPopoverOpen((v) => !v)}
            className="flex h-9 w-9 items-center justify-center rounded-full text-neutral-600 hover:bg-neutral-100"
            aria-label="Démarrer un chrono"
            title="Démarrer un chrono"
          >
            <TimerIcon className="h-5 w-5" />
          </button>
        )}

        {store.status === 'idle' && popoverOpen && (
          <div className="absolute right-0 top-full mt-2 z-40">
            <TimerStartPopover
              onCancel={() => setPopoverOpen(false)}
              onConfirm={(dossierId, taskId) => {
                store.start(dossierId, taskId);
                setPopoverOpen(false);
              }}
            />
          </div>
        )}

        {store.status !== 'idle' && (
          <div className="flex items-center gap-2 rounded-full border border-neutral-200 bg-cime-surface px-3 py-1">
            <span
              className={`h-2 w-2 rounded-full ${store.status === 'running' ? 'animate-pulse bg-red-500' : 'bg-neutral-400'}`}
              aria-hidden
            />
            <span className="text-xs tabular-nums" aria-live="polite">
              {fmt(elapsed)}
            </span>
            {store.status === 'running' ? (
              <button aria-label="Pause" onClick={() => store.pause()} className="text-neutral-600 hover:text-neutral-900">
                <Pause className="h-3.5 w-3.5" />
              </button>
            ) : (
              <button aria-label="Reprendre" onClick={() => store.resume()} className="text-neutral-600 hover:text-neutral-900">
                <Play className="h-3.5 w-3.5" />
              </button>
            )}
            <button
              aria-label="Arrêter"
              onClick={() => {
                const payload = store.stop();
                if (payload) setPendingStop(payload);
              }}
              className="text-red-600 hover:text-red-700"
            >
              <Square className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>

      {pendingStop && (
        <TimeEntryModal
          open={!!pendingStop}
          dossierId={pendingStop.dossierId}
          prefill={prefill}
          onSaveSuccess={() => { savedRef.current = true; }}
          onOpenChange={(o) => {
            if (!o) {
              if (savedRef.current) {
                savedRef.current = false;
                setPendingStop(null);
                return;
              }
              const keep = window.confirm(
                'Enregistrer plus tard ? OK = reprendre le chrono, Annuler = abandonner ce temps.',
              );
              if (keep) {
                useTimerStore.getState().restore(pendingStop!);
              }
              setPendingStop(null);
            }
          }}
        />
      )}
    </>
  );
}
